import React, { useState, useEffect, useRef } from "react";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { LoadingSpinner } from "../../../ui/LoadingSpinner";
import { Dropdown } from "../../../ui/dropdown/Dropdown";
import { DropdownItem } from "../../../ui/dropdown/DropdownItem";

interface BulkRoomServiceItemInput {
  itemname: string;
  category: string;
  description: string;
  ingredients: string;
  priceAmount: number;
  currencyCode: string; // Always "USD"
  isavailable: boolean;
  isNewCategory?: boolean;
}

interface BulkItemResult {
  itemname: string;
  success: boolean;
  error?: string;
  errorMessage?: string;
}

interface BulkAddRoomServiceItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingCategories: string[];
  onSave: (items: BulkRoomServiceItemInput[]) => Promise<BulkItemResult[]>;
  isCreating: boolean;
  error?: string | null;
  retainedRows?: BulkRoomServiceItemInput[];
}

const BulkAddRoomServiceItemsModal: React.FC<
  BulkAddRoomServiceItemsModalProps
> = ({
  isOpen,
  onClose,
  existingCategories,
  onSave,
  isCreating,
  error: externalError,
  retainedRows,
}) => {
  const [rows, setRows] = useState<BulkRoomServiceItemInput[]>([
    {
      itemname: "",
      category: "",
      description: "",
      ingredients: "",
      priceAmount: 0,
      currencyCode: "USD",
      isavailable: true,
    },
  ]);

  const [categories, setCategories] = useState<string[]>(existingCategories);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<{ [key: number]: string[] }>({});
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState<string>("");
  const [editingCategoryRow, setEditingCategoryRow] = useState<number | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [rowHeights, setRowHeights] = useState<{ [key: number]: { description: number; ingredients: number } }>({});
  const [openAvailabilityDropdown, setOpenAvailabilityDropdown] = useState<number | null>(null);
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState<number | null>(null);
  const categoryButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});
  const availabilityButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  // Update categories when existingCategories prop changes
  useEffect(() => {
    setCategories(existingCategories);
  }, [existingCategories]);

  // Hydrate rows with retained rows from parent
  useEffect(() => {
    if (retainedRows && retainedRows.length > 0) {
      setRows(retainedRows);
      // Extract unique categories from retained rows
      const newCategories = new Set(categories);
      retainedRows.forEach((row) => {
        if (row.category && !newCategories.has(row.category)) {
          newCategories.add(row.category);
        }
      });
      setCategories(Array.from(newCategories));
    }
  }, [retainedRows]);

  if (!isOpen) return null;

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        itemname: "",
        category: "",
        description: "",
        ingredients: "",
        priceAmount: 0,
        currencyCode: "USD",
        isavailable: true,
      },
    ]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      newSet.delete(idx);
      // Adjust indices after deletion
      const adjusted = new Set<number>();
      newSet.forEach((i) => {
        if (i > idx) {
          adjusted.add(i - 1);
        } else {
          adjusted.add(i);
        }
      });
      return adjusted;
    });
  };

  const updateRow = (
    idx: number,
    updated: Partial<BulkRoomServiceItemInput>
  ) => {
    setRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...updated } : row))
    );
    // Clear errors for this row when updating
    setRowErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[idx];
      return newErrors;
    });
  };

  const toggleRowSelection = (idx: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === rows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(rows.map((_, i) => i)));
    }
  };


  const enterSelectMode = () => {
    setIsSelectMode(true);
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedRows(new Set());
  };

  const handleResize = (idx: number, field: 'description' | 'ingredients', height: number) => {
    setRowHeights(prev => ({
      ...prev,
      [idx]: {
        ...prev[idx],
        [field]: height || 38 // Default height
      }
    }));
  };

  const applyPriceToSelected = () => {
    if (selectedRows.size === 0) {
      setError("Please select at least one row");
      return;
    }
    const priceInput = prompt("Enter price amount:");
    if (priceInput === null) return;
    const price = parseFloat(priceInput);
    if (isNaN(price) || price < 0) {
      setError("Please enter a valid price (>= 0)");
      return;
    }
    setRows((prev) =>
      prev.map((row, i) =>
        selectedRows.has(i) ? { ...row, priceAmount: price } : row
      )
    );
    setError(null);
  };

  const applyCategoryToSelected = () => {
    if (selectedRows.size === 0) {
      setError("Please select at least one row");
      return;
    }
    const categoryInput = prompt("Enter category name:");
    if (categoryInput === null || categoryInput.trim() === "") return;
    const categoryName = categoryInput.trim();
    setRows((prev) =>
      prev.map((row, i) =>
        selectedRows.has(i) ? { ...row, category: categoryName } : row
      )
    );
    // Add to categories if not exists
    if (!categories.includes(categoryName)) {
      setCategories((prev) => [...prev, categoryName]);
    }
    setError(null);
  };

  const handleCategoryChange = (idx: number, value: string) => {
    if (value === "__NEW__") {
      setEditingCategoryRow(idx);
      setNewCategoryInput("");
    } else {
      updateRow(idx, { category: value });
    }
  };

  const saveNewCategory = (idx: number) => {
    const trimmed = newCategoryInput.trim();
    if (!trimmed) {
      setError("Category name cannot be empty");
      return;
    }
    if (categories.includes(trimmed)) {
      setError("Category already exists");
      return;
    }
    setCategories((prev) => [...prev, trimmed]);
    updateRow(idx, { category: trimmed });
    setEditingCategoryRow(null);
    setNewCategoryInput("");
    setError(null);
  };

  const cancelNewCategory = () => {
    setEditingCategoryRow(null);
    setNewCategoryInput("");
  };

  const handleSave = async () => {
    setError(null);
    setRowErrors({});

    // Validate all rows
    const errors: string[] = [];
    const newRowErrors: { [key: number]: string[] } = {};

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNum = i + 1;
      const rowErrorsList: string[] = [];

      if (!row.itemname.trim()) {
        const msg = "Item name is required";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      } else if (row.itemname.trim().length > 255) {
        const msg = "Item name must be 255 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }

      if (!row.category.trim()) {
        const msg = "Category is required";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      } else if (row.category.trim().length > 100) {
        const msg = "Category must be 100 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }

      if (row.priceAmount <= 0) {
        const msg = "Price must be greater than 0";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }

      if (row.description && row.description.length > 2000) {
        const msg = "Description must be 2000 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }

      if (row.ingredients && row.ingredients.length > 2000) {
        const msg = "Ingredients must be 2000 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }

      if (rowErrorsList.length > 0) {
        newRowErrors[i] = rowErrorsList;
      }
    }

    if (errors.length > 0) {
      setRowErrors(newRowErrors);
      setError(`Please fix the following errors:\n${errors.join("\n")}`);
      return;
    }

    try {
      await onSave(rows);
      // The parent component handles retaining failed rows, so we don't need to filter here
      // The retainedRows prop will be used to rehydrate the modal
    } catch {
      // Let parent error handling surface in externalError
    }
  };

  // CSV import handler
  const handleImportCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) return;

      try {
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length === 0) throw new Error("CSV file is empty");

        // Determine headers
        const headers = lines[0]
          .split(/,|;|\t/)
          .map((h) => h.trim().toLowerCase());
        const idx = (name: string) =>
          headers.findIndex((h) => h === name.toLowerCase());
        const itemnameIdx =
          idx("itemname") !== -1 ? idx("itemname") : idx("item name");
        const categoryIdx = idx("category");
        const priceIdx =
          idx("price") !== -1
            ? idx("price")
            : idx("priceamount") !== -1
            ? idx("priceamount")
            : idx("price amount");
        const descriptionIdx = idx("description");
        const ingredientsIdx = idx("ingredients");
        const isavailableIdx =
          idx("isavailable") !== -1
            ? idx("isavailable")
            : idx("is available") !== -1
            ? idx("is available")
            : idx("available");

        if (itemnameIdx === -1 || categoryIdx === -1 || priceIdx === -1) {
          throw new Error(
            "CSV must include headers: itemname (or 'item name'), category, price (or 'price amount'). Description, ingredients, and isavailable are optional."
          );
        }

        const newRows: BulkRoomServiceItemInput[] = lines.slice(1).map((line) => {
          const cols = line
            .split(/,|;|\t/)
            .map((c) => c.trim())
            .slice(0, headers.length);

          const priceStr = cols[priceIdx] || "0";
          const price = parseFloat(priceStr);
          const priceAmount = isNaN(price) ? 0 : price;

          const isavailableStr = cols[isavailableIdx] || "";
          const isavailable =
            isavailableIdx !== -1 && isavailableStr
              ? isavailableStr.toLowerCase() === "true" ||
                isavailableStr.toLowerCase() === "yes" ||
                isavailableStr === "1"
              : true;

          return {
            itemname: cols[itemnameIdx] || "",
            category: cols[categoryIdx] || "",
            description: cols[descriptionIdx] || "",
            ingredients: cols[ingredientsIdx] || "",
            priceAmount: priceAmount,
            currencyCode: "USD",
            isavailable: isavailable,
          } as BulkRoomServiceItemInput;
        });

        if (newRows.length === 0)
          throw new Error("No data rows found in CSV");

        // Extract unique categories and add to categories list
        const newCategories = new Set(categories);
        newRows.forEach((row) => {
          if (row.category && !newCategories.has(row.category)) {
            newCategories.add(row.category);
          }
        });
        setCategories(Array.from(newCategories));

        // Merge with existing rows
        setRows((prev) => {
          const onlyBlank =
            prev.length === 1 &&
            !prev[0].itemname &&
            !prev[0].category &&
            prev[0].priceAmount === 0;
          return onlyBlank ? newRows : [...prev, ...newRows];
        });

        setError(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to parse CSV";
        setError(msg);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  // Group rows by category for display
  const groupedRows = rows.reduce(
    (acc, row, idx) => {
      const cat = row.category || "Uncategorized";
      if (!acc[cat]) {
        acc[cat] = [];
      }
      acc[cat].push({ row, idx });
      return acc;
    },
    {} as Record<string, Array<{ row: BulkRoomServiceItemInput; idx: number }>>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000] overflow-auto p-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-6xl my-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              Bulk Add Room Service Items
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {(error || externalError) && (
            <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded whitespace-pre-line">
              {externalError || error}
            </div>
          )}

          {/* Select Mode Button */}
          {rows.length > 0 && !isSelectMode && (
            <div className="mb-4">
              <button
                onClick={enterSelectMode}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                Select Items
              </button>
            </div>
          )}

          {/* Bulk Operations Toolbar - Only show in select mode */}
          {isSelectMode && rows.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {selectedRows.size} selected
              </span>
              <button
                onClick={toggleAllRows}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {selectedRows.size === rows.length ? "Deselect All" : "Select All"}
              </button>
              <div className="border-l border-gray-300 dark:border-gray-600 h-6 mx-2" />
              <button
                onClick={applyPriceToSelected}
                disabled={selectedRows.size === 0}
                className="px-3 py-1 text-sm bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Price to Selected
              </button>
              <button
                onClick={applyCategoryToSelected}
                disabled={selectedRows.size === 0}
                className="px-3 py-1 text-sm bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Category to Selected
              </button>
              <button
                onClick={exitSelectMode}
                className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
              >
                Exit Select Mode
              </button>
            </div>
          )}

          {/* Rows table - organized by category */}
          <div className="overflow-x-auto max-h-[50vh] overflow-y-auto border rounded-lg">
            {Object.keys(groupedRows).length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No items added yet. Add rows or import CSV to get started.
              </div>
            ) : (
              Object.entries(groupedRows).map(([category, items]) => (
                <div key={category} className="border-b last:border-b-0">
                  <div className="sticky top-0 bg-gray-100 dark:bg-gray-700 px-4 py-2 font-semibold text-gray-700 dark:text-gray-300">
                    {category || "Uncategorized"}
                  </div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead>
                      <tr>
                        {isSelectMode && (
                          <th className="px-2 py-2 w-12 text-center align-top">
                            <input
                              type="checkbox"
                              checked={
                                items.every((item) =>
                                  selectedRows.has(item.idx)
                                ) && items.length > 0
                              }
                              onChange={() => {
                                const allSelected = items.every((item) =>
                                  selectedRows.has(item.idx)
                                );
                                setSelectedRows((prev) => {
                                  const newSet = new Set(prev);
                                  items.forEach((item) => {
                                    if (allSelected) {
                                      newSet.delete(item.idx);
                                    } else {
                                      newSet.add(item.idx);
                                    }
                                  });
                                  return newSet;
                                });
                              }}
                              className="rounded border-gray-300 mt-1"
                            />
                          </th>
                        )}
                        <th className="px-2 py-2 w-12 text-center align-top">#</th>
                        <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                          Item Name*
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 min-w-[180px]">
                          Category*
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                          Price (USD)*
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                          Available
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                          Description
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                          Ingredients
                        </th>
                        <th className="px-2 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(({ row, idx }) => {
                        const hasRowErrors =
                          rowErrors[idx] && rowErrors[idx].length > 0;
                        return (
                          <React.Fragment key={idx}>
                            <tr
                              className={`border-b border-gray-200 dark:border-gray-700 ${
                                hasRowErrors
                                  ? "bg-red-50 dark:bg-red-900/10"
                                  : ""
                              }`}
                            >
                              {isSelectMode && (
                                <td className="px-2 py-2 text-center align-top">
                                  <input
                                    type="checkbox"
                                    checked={selectedRows.has(idx)}
                                    onChange={() => toggleRowSelection(idx)}
                                    className="rounded border-gray-300 mt-1"
                                  />
                                </td>
                              )}
                              <td className="px-2 py-2 text-center text-gray-600 dark:text-gray-400">
                                <div className="flex items-center justify-center" style={{ height: '38px' }}>{idx + 1}</div>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <input
                                  type="text"
                                  value={row.itemname}
                                  onChange={(e) =>
                                    updateRow(idx, { itemname: e.target.value })
                                  }
                                  maxLength={255}
                                  className={`w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 ${
                                    hasRowErrors ? "border-red-500" : ""
                                  }`}
                                  placeholder="Item name"
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                {editingCategoryRow === idx ? (
                                  <div className="flex gap-1">
                                    <input
                                      type="text"
                                      value={newCategoryInput}
                                      onChange={(e) =>
                                        setNewCategoryInput(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          saveNewCategory(idx);
                                        } else if (e.key === "Escape") {
                                          cancelNewCategory();
                                        }
                                      }}
                                      className="flex-1 p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600"
                                      placeholder="New category"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => saveNewCategory(idx)}
                                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-500"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={cancelNewCategory}
                                      className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-500"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div className="relative inline-block">
                                    <button
                                      ref={(el) => { categoryButtonRefs.current[idx] = el; }}
                                      type="button"
                                      onClick={() => setOpenCategoryDropdown(openCategoryDropdown === idx ? null : idx)}
                                      className={`dropdown-toggle inline-flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm px-4 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 text-gray-800 dark:text-white whitespace-nowrap ${
                                        hasRowErrors ? "border-red-500" : ""
                                      }`}
                                    >
                                      <span>
                                        {row.category || "Select category"}
                                      </span>
                                      <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                                    </button>
                                    <Dropdown
                                      isOpen={openCategoryDropdown === idx}
                                      onClose={() => setOpenCategoryDropdown(null)}
                                      className="min-w-[180px] z-[100002]"
                                      usePortal={true}
                                      buttonRef={{ current: categoryButtonRefs.current[idx] } as React.RefObject<HTMLElement>}
                                    >
                                      <DropdownItem
                                        onClick={() => {
                                          handleCategoryChange(idx, "");
                                          setOpenCategoryDropdown(null);
                                        }}
                                        active={!row.category}
                                      >
                                        Select category
                                      </DropdownItem>
                                      {categories.map((cat) => (
                                        <DropdownItem
                                          key={cat}
                                          onClick={() => {
                                            handleCategoryChange(idx, cat);
                                            setOpenCategoryDropdown(null);
                                          }}
                                          active={row.category === cat}
                                        >
                                          {cat}
                                        </DropdownItem>
                                      ))}
                                      <DropdownItem
                                        onClick={() => {
                                          setEditingCategoryRow(idx);
                                          setNewCategoryInput("");
                                          setOpenCategoryDropdown(null);
                                        }}
                                      >
                                        + Add New
                                      </DropdownItem>
                                    </Dropdown>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                                    $
                                  </span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={
                                      row.priceAmount === 0
                                        ? ""
                                        : row.priceAmount
                                    }
                                    onChange={(e) =>
                                      updateRow(idx, {
                                        priceAmount:
                                          e.target.value === ""
                                            ? 0
                                            : parseFloat(e.target.value) || 0,
                                      })
                                    }
                                    className={`w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 ${
                                      hasRowErrors ? "border-red-500" : ""
                                    }`}
                                    placeholder="0.00"
                                  />
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="relative inline-block">
                                  <button
                                    ref={(el) => { availabilityButtonRefs.current[idx] = el; }}
                                    type="button"
                                    onClick={() => setOpenAvailabilityDropdown(openAvailabilityDropdown === idx ? null : idx)}
                                    className="dropdown-toggle inline-flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm px-4 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 text-gray-800 dark:text-white whitespace-nowrap"
                                  >
                                    <span
                                      className={`w-2.5 h-2.5 rounded-full ${
                                        row.isavailable ? "bg-green-500" : "bg-red-500"
                                      }`}
                                    />
                                    <span>
                                      {row.isavailable ? "Available" : "Unavailable"}
                                    </span>
                                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                                  </button>
                                  <Dropdown
                                    isOpen={openAvailabilityDropdown === idx}
                                    onClose={() => setOpenAvailabilityDropdown(null)}
                                    className="min-w-[160px] z-[100002]"
                                    usePortal={true}
                                    buttonRef={{ current: availabilityButtonRefs.current[idx] } as React.RefObject<HTMLElement>}
                                  >
                                    <DropdownItem
                                      onClick={() => {
                                        updateRow(idx, {
                                          isavailable: true,
                                        });
                                        setOpenAvailabilityDropdown(null);
                                      }}
                                      active={row.isavailable === true}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-green-500" />
                                        Available
                                      </div>
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={() => {
                                        updateRow(idx, {
                                          isavailable: false,
                                        });
                                        setOpenAvailabilityDropdown(null);
                                      }}
                                      active={row.isavailable === false}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-red-500" />
                                        Unavailable
                                      </div>
                                    </DropdownItem>
                                  </Dropdown>
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <textarea
                                  value={row.description}
                                  onChange={(e) =>
                                    updateRow(idx, {
                                      description: e.target.value,
                                    })
                                  }
                                  onMouseUp={(e) => {
                                    const textarea = e.target as HTMLTextAreaElement;
                                    handleResize(idx, 'description', textarea.offsetHeight);
                                  }}
                                  maxLength={2000}
                                  className="w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600"
                                  style={{ resize: 'vertical', height: rowHeights[idx]?.description || 38, minHeight: 38 }}
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <textarea
                                  value={row.ingredients}
                                  onChange={(e) =>
                                    updateRow(idx, {
                                      ingredients: e.target.value,
                                    })
                                  }
                                  onMouseUp={(e) => {
                                    const textarea = e.target as HTMLTextAreaElement;
                                    handleResize(idx, 'ingredients', textarea.offsetHeight);
                                  }}
                                  maxLength={2000}
                                  className="w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600"
                                  style={{ resize: 'vertical', height: rowHeights[idx]?.ingredients || 38, minHeight: 38 }}
                                  placeholder="Ingredients"
                                />
                              </td>
                              <td className="px-1 py-2 text-center">
                                {rows.length > 1 && (
                                  <button
                                    onClick={() => removeRow(idx)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                            {hasRowErrors && (
                              <tr>
                                <td colSpan={isSelectMode ? 9 : 8} className="px-3 pt-1 pb-2">
                                  <div className="text-xs text-red-600 whitespace-pre-line">
                                    {rowErrors[idx]?.join("\n")}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>

          {/* Bottom actions */}
          <div className="mt-4 flex flex-wrap justify-between items-center gap-3">
            {/* Left side actions */}
            <div className="flex gap-2">
              <button
                onClick={addRow}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                + Add Row
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
                >
                  Import CSV
                </button>
                <button
                  onClick={() => setShowCSVModal(true)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                  title="View CSV format example"
                >
                  Learn More
                </button>
              </div>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleImportCsv}
                className="hidden"
              />
            </div>

            {/* Right side actions */}
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={isCreating}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isCreating}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600"
              >
                {isCreating ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" color="primary" />
                    <span>Adding...</span>
                  </div>
                ) : (
                  "Add Room Service Items"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CSV Format Modal */}
      {showCSVModal && (
        <RoomServiceItemsCSVFormatModal
          isOpen={showCSVModal}
          onClose={() => setShowCSVModal(false)}
        />
      )}
    </>
  );
};

// CSV Format Modal Component
const RoomServiceItemsCSVFormatModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const csvExample = `itemname,category,price,isavailable,description,ingredients
Caesar Salad,Salads,12.99,true,Fresh romaine lettuce with caesar dressing,Romaine lettuce, caesar dressing, parmesan, croutons
Grilled Chicken,Main Courses,24.99,true,Tender grilled chicken breast,Chicken breast, olive oil, salt, pepper
Chocolate Cake,Desserts,8.99,false,Rich chocolate cake with frosting,Flour, sugar, cocoa, eggs, butter`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100001] p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-2xl w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            What is the correct CSV format for room service items?
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Your CSV file should include the following headers (description,
            ingredients, and isavailable are optional; isavailable defaults to
            true):
          </p>
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
            <code className="text-sm text-gray-800 dark:text-gray-200">
              itemname, category, price, isavailable, description, ingredients
            </code>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Example CSV Format:
          </label>
          <textarea
            value={csvExample}
            readOnly
            className="w-full h-40 p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 font-mono text-sm resize-none"
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-500 dark:hover:bg-blue-600"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAddRoomServiceItemsModal;

